<i18n>
{
  "fr": {
    "in-layer-results": "Dans:",
    "color": "Couleur",
    "grey": "Gris",
    "aerial": "Aérienne"
  },
  "de": {

  }
}
</i18n>

<template>
  <div>
    <Map :layers="layers" :projection="projection" :viewExtent="viewExtent" :center="center" :defaultZoom="defaultZoom"
      :minZoom="minZoom" :maxZoom="maxZoom" :interactions="interactions" :overlays="overlays" @init="onMapInit">
      <template v-slot:map-tool>
        <div class="maptool-row">
          <div class="background-selector">
            <div :class="['background-map', { 'selected': backgroundLayer.name === 'ch.swisstopo.pixelkarte-farbe' }]"
              @click="setBackgroundMap('ch.swisstopo.pixelkarte-farbe')">
              <div class="img-wrapper"><img src="@/assets/images/mapPreview/farbe.png" /></div>
              <span class="map-title">{{ $t('color') }}</span>
            </div>
            <div :class="['background-map', { 'selected': backgroundLayer.name === 'ch.swisstopo.pixelkarte-grau' }]"
              @click="setBackgroundMap('ch.swisstopo.pixelkarte-grau')">
              <div class="img-wrapper"><img src="@/assets/images/mapPreview/grau.png" /></div>
              <span class="map-title">{{ $t('grey') }}</span>
            </div>
            <div :class="['background-map', { 'selected': backgroundLayer.name === 'ch.swisstopo.swissimage' }]"
              @click="setBackgroundMap('ch.swisstopo.swissimage')">
              <div class="img-wrapper"><img src="@/assets/images/mapPreview/aerial.png" /></div>
              <span class="map-title">{{ $t('aerial') }}</span>
            </div>
          </div>
          <div class="time-selector" v-if="hasTemporalLayers">
            <md-progress-spinner v-if="timeframeLoading" class="time-loading" :md-diameter="20" :md-stroke="3"
              md-mode="indeterminate"></md-progress-spinner>
            <Timeframe v-if="!timeframeLoading" ref="timeframe" mode="discrete" :halfWindowSize="halfWindowSize"
              :enableZoom="false" :showTimeline="false" />
          </div>
        </div>
      </template>
    </Map>
    <div id="popup" class="ol-popup">
      <a href="#" id="popup-closer" class="ol-popup-closer" @click="closeSelectOverlay"></a>
      <div id="popup-content" v-if="selectedFeatures.length > 0">
        <div class="in-layer" v-for="result in selectedFeatures" :key="result.layer.id">
          <h3 v-if="result.features.length">{{ $t('in-layer-results') }} {{ result.layer.metadata.name }}</h3>
          <div class="result-items">
            <div class="result-item" v-for="item in result.features" :key="item.properties.oid"
              @click="selectFeature(item, result.layer)">
              <GeomTypeIcon class="geom-type-icon" :geomType="result.layer.geomType" />
              {{ item.properties.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import _ from 'lodash'
import moment from 'moment'

import LayerStoreMixin from '@/mixins/store/layer'
import MapStoreMixin from '@/mixins/store/map'
import CommonStoreMixin from '@/mixins/store/common'
import TimeStoreMixin from '@/mixins/store/time'
import MapMixin from '@/mixins/store/map'
import InteractionMixin from '@/mixins/store/interaction'
import LayerMixin from '@/mixins/store/layer'

import Map from '@/components/vue-map/map.vue'
import Layer from '@/components/vue-map/layers/layer'
import GeomTypeIcon from '@/components/vue-map/common/geomTypeIcon'
import Timeframe from '@/components/tool/timeframe'

import LayerGroup from 'ol/layer/Group'
import SwisstopoLayer from '@/components/vue-map/layers/swissTopo'
import Overlay from 'ol/Overlay'
// import { TileDebug } from 'ol/source'
// import TileLayer from 'ol/layer/Tile'

import Config from '@/config.js'

import camelize from 'camelize'

export default {
  name: 'MapView',
  mixins: [
    LayerStoreMixin,
    MapStoreMixin,
    CommonStoreMixin,
    TimeStoreMixin,
    MapMixin,
    InteractionMixin,
    LayerMixin
  ],

  components: {
    Map,
    GeomTypeIcon,
    Timeframe
  },
  computed: {
    overlays () {
      let toReturn = []
      if (this.selectOverlay !== null) toReturn.push(this.selectOverlay)
      return toReturn
    }
  },
  data () {
    return {
      projection: Config.mapConfiguration.projection,
      viewExtent: Config.mapConfiguration.view.extent,
      center: Config.mapConfiguration.center,
      defaultZoom: Config.mapConfiguration.view.zoom,
      minZoom: Config.mapConfiguration.view.minZoom,
      maxZoom: Config.mapConfiguration.view.maxZoom,
      layers: [],
      selectOverlay: null,
      selectedFeatures: [],
      timeframeLoading: false,
      halfWindowSize: moment.duration(5, 'months')
    }
  },
  mounted () {
    let backgroundLayers = new LayerGroup({
      layers: [
        new SwisstopoLayer(this.backgroundLayer.name)
      ]
    })
    backgroundLayers.name = Layer.backgroundLayersName

    let userLayers = new LayerGroup()
    userLayers.name = Layer.userLayersName

    this.layers = [
      backgroundLayers,
      userLayers
      // new TileLayer({
      //   source: new TileDebug(),
      // })
    ]

    this.selectOverlay =  new Overlay({
      element: window.document.getElementById('popup'),
      autoPan: true,
      autoPanAnimation: {
        duration: 250,
      },
    })
  },
  watch: {
    layerGroups () {
      let userLayers = Layer.getUserLayers(this.mapRef).getLayers()
      if (this.layerGroups.length === 0) this.setCurrentGeom(null)

      userLayers.clear()
      this.layerGroups.forEach((lg) => {
        userLayers.push(lg)
      })

      // Mise à jour le sélecteur temporel
      this.timeframeLoading = true
      this.updateTimeframeForLayer(this.temporalLayers).then((timeRange) => {
        this.timeframeLoading = false
        if (timeRange === null) return

        this.$nextTick(() => {
          let dateFrom = timeRange.dateFrom.clone()
          let dateTo = timeRange.dateFrom.clone()
          this.halfWindowSize = this.getHalfWindowSizeFromTimeResolution(timeRange.timeResolution)

          dateFrom.subtract(this.halfWindowSize)
          dateTo.add(this.halfWindowSize)
          if (typeof this.$refs.timeframe !== 'undefined') this.$refs.timeframe.moveTo([ dateFrom, dateTo ])
        })
      }).catch(this.onError)
    },
    'backgroundLayer.name': function () {
        let backgroundLayers = Layer.getBackgoundLayers(this.mapRef).getLayers()

        backgroundLayers.clear()
        backgroundLayers.push(new SwisstopoLayer(this.backgroundLayer.name))
    },
    dateMiddle () {
      this.updateTimestampOnLayer(this.dateMiddle)
    },
    currentShownLayers () {
      if (this.currentGeom !== null && this.currentGeom.type === 'Point') setTimeout(() => this.findFeaturesOnLayers(this.currentGeom.coordinates), 100)
    }
  },
  methods: {
    onMapInit (mapRef) {
      this.initMap(mapRef)
      this.initInteractions(mapRef)
      this.vectorClickInteraction.onSelect((event) => this.findFeaturesOnLayers(event.coordinate))
    },
    findFeaturesOnLayers (coordinate) {
      window.document.body.style.cursor = 'wait'

      let layerToFetch = this.currentShownLayers.filter((layer) => layer.webviewConfig.availableInteractions.find(interaction => interaction.key === 'POINT'))
      let toFetch = layerToFetch.map(layer => layer.getFeaturesFromCoordinates(this.mapRef, coordinate))

      Promise.all(toFetch).then((data) => {
        let selectedFeatures = []
        let features = []
        let tmp

        // Assignation du layer aux features retournées
        data.forEach((d, i) => {
          tmp = {
            layer: layerToFetch[i],
            features: []
          }
          d.forEach((feature) => {
            feature.layer = layerToFetch[i];
            tmp.features.push(feature);
            features.push(feature)
          })
          selectedFeatures.push(tmp)
        })

        // Pas de features trouvé, on ne fait rien
        if (features.length === 0) return

        // Plusieurs features trouvées, on affiche un popup pour sélection
        if (features.length > 1) {
          this.selectedFeatures = selectedFeatures
          this.selectOverlay.setPosition(coordinate)
          return
        }

        // Qu'une seule feature, on la sélectionne
        let toSelect = _.head(features)
        this.selectFeature(toSelect, toSelect.layer)
      }).finally(() => {
        window.document.body.style.cursor = 'default'
      })
    },
    closeSelectOverlay () {
      this.selectOverlay.setPosition(undefined)
      window.document.getElementById('popup-closer').blur()
    },
    selectFeature (item, layer) {
      this.setCurrentGeom(item.geometry)
      this.setCurrentObject(camelize(item.properties))
      this.centerTo(item.geometry.coordinates, layer.webviewConfig.zoomToFeature)
      this.selectLayer(layer)
      this.closeSelectOverlay()
    },
    setBackgroundMap (name) {
      this.$store.commit('setBackgroundLayerName', name)
    },
    onError (e) {
      console.error(e)
    },
    updateTimestampOnLayer (event) {
      this.temporalLayers.forEach((layer) => {
        layer.setTimestamp(event)
      })
    }
  }
}
</script>

<style lang="scss">
.result-item {
  cursor: pointer;
}

#map {
  width: 100%;
  height: 100vh;
}

.bottomMapTool {
  position: absolute;
  bottom: 0;
  left: 10px;
}

.left-panel-open .bottomMapTool {
  left: 393px;
}
</style>

<style lang="scss" scoped>
.ol-popup {
  position: absolute;
  background-color: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  padding: 15px;
  border-radius: 10px;
  border: 1px solid #cccccc;
  bottom: 12px;
  left: -50px;
  min-width: 250px;
}

.ol-popup:after,
.ol-popup:before {
  top: 100%;
  border: solid transparent;
  content: " ";
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
}

.ol-popup:after {
  border-top-color: white;
  border-width: 10px;
  left: 48px;
  margin-left: -10px;
}

.ol-popup:before {
  border-top-color: #cccccc;
  border-width: 11px;
  left: 48px;
  margin-left: -11px;
}

.ol-popup-closer {
  text-decoration: none;
  position: absolute;
  top: 2px;
  right: 8px;
}

.ol-popup-closer:after {
  content: "✖";
}

#popup-content {
  h3 {
    margin: 0;
    font-size: 14px;
  }
}

.background-selector {
  display: flex;
  margin-bottom: 10px;
  width: 90px;

  &:hover {
    width: auto;

    .background-map {
      display: block;
    }
  }

  .background-map {
    width: 80px;
    height: 80px;
    margin-right: 10px;
    overflow: hidden;
    border-radius: 3px;
    display: none;
    cursor: pointer;
    user-select: none;
    border: 4px solid #ffffffcc;

    &.selected {
      display: block;
    }

    .img-wrapper {
      width: 100%;
      height: calc(100% - 18px);
      overflow: hidden;

      img {
        object-fit: cover;
        transform: scale(8) translateX(-2px) translateY(3px);
      }
    }

    .map-title {
      width: 100%;
      background-color: #ffffffcc;
      color: black;
      height: 18px;
      display: block;
      text-align: center;
      font-size: 12px;
    }
  }
}

.maptool-row {
  display: flex;
  align-items: flex-end;
}

.time-selector {
  background-color: #ffffffcc;
  border-radius: 3px;
  width: 220px;
  height: 25px;
  margin-bottom: 10px;

  .time-loading {
    margin-left: calc(50% - 20px);
    margin-top: 3px;
  }
}
</style>
